import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box, Button, Card, Center, Flex, HStack, Icon, IconButton,
    Input, InputGroup, InputLeftElement, Select, Spinner, Table, Tbody,
    Td, Text, Th, Thead, Tr, TableContainer, useColorModeValue,
    useDisclosure, useToast, VStack, Badge,
} from "@chakra-ui/react";
import {
    MdSearch, MdChevronLeft, MdChevronRight, MdVisibility,
} from "react-icons/md";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { BASE_URL } from "config/api";
import { usePermissions } from "hooks/usePermissions";
import { useNavbarAction } from "contexts/NavbarActionContext";
import ReclamationDetailModal from "./ReclamationDetailModal";
import CreateReclamationModal from "./CreateReclamationModal";

// ── Badges ─────────────────────────────────────────────────
const STATUT_CONFIG = {
    Ouverte  : { bg: "#EBF4FF", border: "#BEE3F8", text: "#1A56DB", label: "Ouverte"   },
    "En cours": { bg: "#FFF9E7", border: "#FFE899", text: "#8D6E00", label: "En cours"  },
    Resolue  : { bg: "#EDF7F1", border: "#CDE8D9", text: "#006133", label: "Résolue"   },
    Cloturee : { bg: "#F7F7F7", border: "#E2E8F0", text: "#6B7280", label: "Clôturée"  },
};
const GRAVITE_CONFIG = {
    Critique: { colorScheme: "red",    label: "Critique" },
    Moyen   : { colorScheme: "orange", label: "Moyen"    },
    Faible  : { colorScheme: "green",  label: "Faible"   },
};

const StatutBadge = ({ statut }) => {
    const cfg = STATUT_CONFIG[statut] || STATUT_CONFIG["Ouverte"];
    return (
        <HStack spacing={2} bg={cfg.bg} border="1px solid" borderColor={cfg.border}
            px={3} py={1} borderRadius="full" w="fit-content">
            <Box w="7px" h="7px" borderRadius="full" bg={cfg.text} />
            <Text fontSize="11px" fontWeight="600" color={cfg.text}>{cfg.label}</Text>
        </HStack>
    );
};

const GraviteBadge = ({ gravite }) => {
    const cfg = GRAVITE_CONFIG[gravite];
    if (!cfg) return null;
    return <Badge colorScheme={cfg.colorScheme} variant="subtle" borderRadius="full" px={2}>{cfg.label}</Badge>;
};

// ── Composant principal ─────────────────────────────────────
export default function Reclamations() {
    const { showStats, setOnCreateReclamation } = useNavbarAction();
    const { hasPermission, loading: permissionsLoading } = usePermissions();
    const toast = useToast();
    const bgColor = useColorModeValue("gray.50", "gray.800");
    const cardBg  = useColorModeValue("white", "gray.700");

    // Onglet actif
    const [activeTab, setActiveTab] = useState("actives"); // "actives" | "archivees"

    // Données
    const [tableData, setTableData]   = useState([]);
    const [loading, setLoading]       = useState(true);
    const [selectedId, setSelectedId] = useState(null);

    // Filtres
    const [searchTerm, setSearchTerm]               = useState("");
    const [statutFilter, setStatutFilter]           = useState("all");
    const [typeFilter, setTypeFilter]               = useState("all");
    const [graviteFilter, setGraviteFilter]         = useState("all");
    const [dateFrom, setDateFrom]                   = useState("");
    const [dateTo, setDateTo]                       = useState("");

    // Pagination
    const [currentPage, setCurrentPage]   = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Modales
    const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();

    // Ouvrir le détail via URL param ?open=ID (depuis notif ou lien externe)
    const location = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const openId = params.get("open");
        if (openId) {
            setSelectedId(Number(openId));
            onDetailOpen();
            // Nettoyer l'URL sans déclencher React Router → pas de re-run de l'effet
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, [location.search, onDetailOpen]);

    // Ouvrir le détail via navigation state (fallback)
    useEffect(() => {
        const id = location.state?.openReclamationId;
        if (id) {
            setSelectedId(Number(id));
            onDetailOpen();
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, [location.state, onDetailOpen]);

    // Enregistrer le callback "Nouvelle réclamation" dans la navbar (seulement sur onglet actives)
    useEffect(() => {
        if (activeTab === "actives") {
            setOnCreateReclamation(() => onCreateOpen);
        } else {
            setOnCreateReclamation(null);
        }
        return () => setOnCreateReclamation(null);
    }, [activeTab, onCreateOpen, setOnCreateReclamation]);

    // ── Chargement ──
    const fetchReclamations = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (activeTab === "archivees") {
                params.include_archived = "true";
            }
            if (statutFilter !== "all")  params.statut           = statutFilter;
            if (typeFilter   !== "all")  params.type_reclamation = typeFilter;
            if (graviteFilter !== "all") params.niveau_gravite   = graviteFilter;
            if (dateFrom)                params.date_from        = dateFrom;
            if (dateTo)                  params.date_to          = dateTo;

            const res = await axios.get(`${BASE_URL}/reclamations`, { params, withCredentials: true });
            // Filtrer selon l'onglet
            const data = activeTab === "archivees"
                ? res.data.filter((r) => r.archivee_at)
                : res.data;
            setTableData(data);
        } catch {
            toast({ title: "Erreur lors du chargement", status: "error", isClosable: true });
        } finally {
            setLoading(false);
        }
    }, [activeTab, statutFilter, typeFilter, graviteFilter, dateFrom, dateTo, toast]);

    useEffect(() => {
        if (permissionsLoading) return;
        if (!hasPermission("reclamations_view")) return;
        fetchReclamations();
    }, [permissionsLoading, hasPermission, fetchReclamations]);

    // Reset page quand on change d'onglet
    useEffect(() => {
        setCurrentPage(1);
        setSearchTerm("");
        setStatutFilter("all");
        setTypeFilter("all");
        setGraviteFilter("all");
        setDateFrom("");
        setDateTo("");
    }, [activeTab]);

    // ── Stats ──
    const stats = useMemo(() => ({
        total   : tableData.length,
        ouverte : tableData.filter((r) => r.statut === "Ouverte").length,
        encours : tableData.filter((r) => r.statut === "En cours").length,
        resolue : tableData.filter((r) => r.statut === "Resolue").length,
        cloturee: tableData.filter((r) => r.statut === "Cloturee").length,
    }), [tableData]);

    // ── Filtre local (recherche texte) ──
    const processedData = useMemo(() => {
        if (!searchTerm.trim()) return tableData;
        const q = searchTerm.toLowerCase();
        return tableData.filter((r) =>
            r.numero_reclamation?.toLowerCase().includes(q) ||
            r.type_reclamation?.toLowerCase().includes(q)  ||
            r.reference_dossier?.toLowerCase().includes(q) ||
            r.createur?.nom?.toLowerCase().includes(q)     ||
            r.createur?.prenom?.toLowerCase().includes(q)
        );
    }, [tableData, searchTerm]);

    const totalPages   = Math.ceil(processedData.length / itemsPerPage);
    const paginatedData = useMemo(() => {
        const s = (currentPage - 1) * itemsPerPage;
        return processedData.slice(s, s + itemsPerPage);
    }, [processedData, currentPage, itemsPerPage]);

    const handleView = (id) => { setSelectedId(id); onDetailOpen(); };
    const handleCreated = () => { fetchReclamations(); };

    const fmtDate = (dt) => dt ? new Date(dt).toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    }) : "—";

    // ── Guards ──
    if (permissionsLoading) return <Flex justify="center" align="center" h="100vh"><Spinner size="xl" color="red.500" /></Flex>;
    if (!hasPermission("reclamations_view")) return (
        <Flex justify="center" align="center" h="100vh" direction="column">
            <Text fontSize="xl" fontWeight="bold" color="red.500">Accès refusé</Text>
            <Text>Vous n'avez pas la permission de consulter cette page.</Text>
        </Flex>
    );

    return (
        <Box minH="100vh" bg={bgColor} pt={{ base: "200px", md: "160px", xl: "160px" }} px={6} pb={6}>
            <Box maxW="9xl" mx="auto">
                <VStack spacing={6} align="stretch">



                    {/* Stats */}
                    {showStats && (
                        <HStack spacing={4} flexWrap="wrap">
                            {[
                                { label: "Total",     value: stats.total,    color: "gray.700"  },
                                { label: "Ouvertes",  value: stats.ouverte,  color: "#1A56DB"   },
                                { label: "En cours",  value: stats.encours,  color: "#8D6E00"   },
                                { label: "Résolues",  value: stats.resolue,  color: "#006133"   },
                                { label: "Clôturées", value: stats.cloturee, color: "#6B7280"   },
                            ].map(({ label, value, color }) => (
                                <Card key={label} bg={cardBg} borderRadius="10px" px={5} py={3} minW="110px" flex="1">
                                    <Text fontSize="xs" color="gray.500">{label}</Text>
                                    <Text fontSize="2xl" fontWeight="bold" color={color}>{value}</Text>
                                </Card>
                            ))}
                        </HStack>
                    )}

                    {/* Tabs */}
                    <HStack spacing={0} bg={cardBg} borderRadius="10px" border="1px solid" borderColor="gray.200" p="4px" w="fit-content">
                        <Button
                            size="sm"
                            h="32px"
                            px={4}
                            borderRadius="7px"
                            fontWeight="600"
                            fontSize="13px"
                            fontFamily="Poppins, sans-serif"
                            bg={activeTab === "actives" ? "#E53E3E" : "transparent"}
                            color={activeTab === "actives" ? "white" : "gray.500"}
                            _hover={activeTab === "actives" ? {} : { bg: "gray.100" }}
                            onClick={() => setActiveTab("actives")}
                        >
                            Réclamations actives
                        </Button>
                        <Button
                            size="sm"
                            h="32px"
                            px={4}
                            borderRadius="7px"
                            fontWeight="600"
                            fontSize="13px"
                            fontFamily="Poppins, sans-serif"
                            bg={activeTab === "archivees" ? "#E53E3E" : "transparent"}
                            color={activeTab === "archivees" ? "white" : "gray.500"}
                            _hover={activeTab === "archivees" ? {} : { bg: "gray.100" }}
                            onClick={() => setActiveTab("archivees")}
                        >
                            Réclamations archivées
                        </Button>
                    </HStack>

                    {/* Filtres */}
                    <Card bg={cardBg} borderRadius="12px" p={5}>
                        <VStack align="stretch" spacing={4}>
                            <Text fontSize="lg" fontWeight="bold" color="gray.900">
                                {activeTab === "actives" ? "Liste des réclamations" : "Réclamations archivées"}
                            </Text>

                            <Flex gap={3} flexWrap="wrap">
                                <InputGroup flex={1} minW="200px">
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={MdSearch} color="gray.400" />
                                    </InputLeftElement>
                                    <Input placeholder="Numéro, type, référence, créateur..." value={searchTerm}
                                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                                </InputGroup>

                                {activeTab === "actives" && (
                                    <Select value={statutFilter} onChange={(e) => { setStatutFilter(e.target.value); setCurrentPage(1); }} w="160px">
                                        <option value="all">Statut: Tous</option>
                                        <option value="Ouverte">Ouverte</option>
                                        <option value="En cours">En cours</option>
                                        <option value="Resolue">Résolue</option>
                                        <option value="Cloturee">Clôturée</option>
                                    </Select>
                                )}

                                <Select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }} w="185px">
                                    <option value="all">Type: Tous</option>
                                    <option value="Delai">Délai</option>
                                    <option value="Erreur medicale">Erreur médicale</option>
                                    <option value="Communication">Communication</option>
                                    <option value="Materiel">Matériel</option>
                                    <option value="Autre">Autre</option>
                                </Select>

                                <Select value={graviteFilter} onChange={(e) => { setGraviteFilter(e.target.value); setCurrentPage(1); }} w="160px">
                                    <option value="all">Gravité: Toutes</option>
                                    <option value="Critique">Critique</option>
                                    <option value="Moyen">Moyen</option>
                                    <option value="Faible">Faible</option>
                                </Select>

                                <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }} w="155px" placeholder="Date début" />
                                <Input type="date" value={dateTo}   onChange={(e) => { setDateTo(e.target.value);   setCurrentPage(1); }} w="155px" placeholder="Date fin" />
                            </Flex>

                            {/* Tableau */}
                            {loading ? (
                                <Center py={10}><Spinner size="xl" color="red.500" /></Center>
                            ) : paginatedData.length === 0 ? (
                                <Center py={10}><Text color="gray.500">
                                    {activeTab === "archivees" ? "Aucune réclamation archivée" : "Aucune réclamation trouvée"}
                                </Text></Center>
                            ) : (
                                <TableContainer border="1px solid" borderColor="gray.200" borderRadius="12px">
                                    <Table variant="simple" sx={{
                                        "th, td": { borderRight: "1px solid", borderColor: "gray.200", whiteSpace: "nowrap" },
                                        "th:last-child, td:last-child": { borderRight: "none" },
                                        "tr:last-child td": { borderBottom: "none" },
                                    }}>
                                        <Thead>
                                            <Tr bg="gray.50">
                                                <Th>Numéro</Th>
                                                <Th>Date création</Th>
                                                <Th>Type</Th>
                                                <Th>Gravité</Th>
                                                <Th>Service</Th>
                                                {activeTab === "actives"
                                                    ? <Th>Statut</Th>
                                                    : <Th>Date archivage</Th>
                                                }
                                                <Th>Assigné à</Th>
                                                <Th>Actions</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {paginatedData.map((rec) => (
                                                <Tr key={rec.id} _hover={{ bg: "gray.50" }} cursor="pointer"
                                                    onClick={() => handleView(rec.id)}>
                                                    <Td>
                                                        <VStack align="start" spacing={0}>
                                                            <Text fontWeight="semibold" fontSize="sm" color="red.600">
                                                                {rec.numero_reclamation}
                                                            </Text>
                                                            {rec.archivee_at && (
                                                                <Badge colorScheme="gray" variant="subtle" fontSize="9px">Archivée</Badge>
                                                            )}
                                                        </VStack>
                                                    </Td>
                                                    <Td fontSize="sm" color="gray.600">{fmtDate(rec.created_at)}</Td>
                                                    <Td>
                                                        <Badge colorScheme="purple" variant="subtle" borderRadius="full" px={2} fontSize="11px">
                                                            {rec.type_reclamation}
                                                        </Badge>
                                                    </Td>
                                                    <Td><GraviteBadge gravite={rec.niveau_gravite} /></Td>
                                                    <Td fontSize="sm" color="gray.700">{rec.service_concerne}</Td>
                                                    <Td>
                                                        {activeTab === "actives"
                                                            ? <StatutBadge statut={rec.statut} />
                                                            : <Text fontSize="sm" color="gray.500">{fmtDate(rec.archivee_at)}</Text>
                                                        }
                                                    </Td>
                                                    <Td fontSize="sm" color="gray.700">
                                                        {rec.assigne_a
                                                            ? `${rec.assigne_a.prenom || ""} ${rec.assigne_a.nom || ""}`.trim()
                                                            : <Text color="gray.400" fontSize="xs">Non assigné</Text>
                                                        }
                                                    </Td>
                                                    <Td onClick={(e) => e.stopPropagation()}>
                                                        <IconButton
                                                            icon={<MdVisibility />}
                                                            variant="ghost"
                                                            size="sm"
                                                            aria-label="Voir"
                                                            colorScheme="blue"
                                                            onClick={() => handleView(rec.id)}
                                                        />
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}

                            {/* Pagination */}
                            {!loading && processedData.length > 0 && (
                                <Flex justify="space-between" align="center" pt={2}>
                                    <HStack spacing={2}>
                                        <Text fontSize="sm" color="gray.600">Afficher</Text>
                                        <Select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                            w="90px" size="sm">
                                            {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                                        </Select>
                                        <Text fontSize="sm" color="gray.600">par page</Text>
                                    </HStack>
                                    <Text fontSize="sm" color="gray.600">
                                        {((currentPage - 1) * itemsPerPage) + 1}–{Math.min(currentPage * itemsPerPage, processedData.length)} sur {processedData.length}
                                    </Text>
                                    <HStack spacing={1}>
                                        <IconButton icon={<MdChevronLeft />} size="sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1} aria-label="Précédent" />
                                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                            const n = i + 1;
                                            return (
                                                <Button key={n} size="sm" onClick={() => setCurrentPage(n)}
                                                    colorScheme={currentPage === n ? "red" : "gray"}
                                                    variant={currentPage === n ? "solid" : "outline"}>
                                                    {n}
                                                </Button>
                                            );
                                        })}
                                        <IconButton icon={<MdChevronRight />} size="sm" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages || totalPages === 0} aria-label="Suivant" />
                                    </HStack>
                                </Flex>
                            )}
                        </VStack>
                    </Card>
                </VStack>
            </Box>

            {/* Modale détail */}
            <ReclamationDetailModal
                isOpen={isDetailOpen}
                onClose={onDetailClose}
                reclamationId={selectedId}
                onUpdated={fetchReclamations}
            />

            {/* Modale création */}
            <CreateReclamationModal
                isOpen={isCreateOpen}
                onClose={onCreateClose}
                onCreated={handleCreated}
            />
        </Box>
    );
}
